/**
 * Phase 2/3/4 — Unified Search + Seasons Hub + Backup & Audit
 * ----------------------------------------------------------------
 * Lives next to the SmartArchiveManagement page. Contains 3 tabs:
 *   - 🔎 بحث موحد   (search across operations, clients, installments)
 *   - 📅 المواسم    (seasons hub with stats + filter)
 *   - 🛡️ النسخ الاحتياطية + سجل التدقيق  (super_admin only)
 */
import React, { useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Search, Calendar, Shield, Download, RefreshCw, AlertTriangle, Database } from 'lucide-react';
import { AuthContext, API, formatDateWithEnglishNumerals } from '../shared';
import LifecycleBadge from './LifecycleBadge';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const OperationsHub = () => {
  const { user } = useContext(AuthContext);

  if (user?.role === 'agency_staff') {
    return (
      <div className="p-6" dir="rtl">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-bold">⛔ هذه الصفحة للمدير العام والمحاسب فقط</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="ops-hub-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-7 w-7 text-brand-600" />
          🌐 مركز العمليات الموحّد
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          بحث موحّد — مركز المواسم — النسخ الاحتياطية وسجل التدقيق
        </p>
      </div>

      <Tabs defaultValue="search" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="search" data-testid="tab-search">🔎 بحث موحّد</TabsTrigger>
          <TabsTrigger value="seasons" data-testid="tab-seasons">📅 المواسم</TabsTrigger>
          {user?.role === 'super_admin' && (
            <TabsTrigger value="backup" data-testid="tab-backup">🛡️ النسخ الاحتياطية</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="search"><UnifiedSearchTab /></TabsContent>
        <TabsContent value="seasons"><SeasonsTab /></TabsContent>
        {user?.role === 'super_admin' && (
          <TabsContent value="backup"><BackupAuditTab /></TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// ----------------------------- Tab 1: Unified Search -----------------------------
const UnifiedSearchTab = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(true);

  const search = useCallback(async () => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lifecycle/search`, {
        ...auth(),
        params: { q, include_archived: includeArchived, limit: 100 },
      });
      setResults(r.data || []);
    } catch (e) {
      console.error('search failed', e);
    } finally {
      setLoading(false);
    }
  }, [q, includeArchived]);

  useEffect(() => {
    const t = setTimeout(search, 400); // debounce
    return () => clearTimeout(t);
  }, [search]);

  const typeLabel = {
    operation: { icon: '📄', label: 'عملية يومية', color: 'bg-brand-100 text-brand-800' },
    client: { icon: '👤', label: 'عميل', color: 'bg-green-100 text-green-800' },
    installment_receipt: { icon: '💳', label: 'وصل أقساط', color: 'bg-purple-100 text-purple-800' },
    trip: { icon: '✈️', label: 'رحلة', color: 'bg-amber-100 text-amber-800' },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5" /> 🔎 بحث موحّد</CardTitle>
        <CardDescription>
          اكتب اسم عميل، رقم وصل، أو خدمة — يبحث عبر كل المواسم (نشطة + مؤرشفة) في وقت واحد
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Search className="h-5 w-5 text-brand-600" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="🔍 ابحث: عميل، رقم وصل، خدمة..."
            className="flex-1 text-right"
            data-testid="unified-search-input"
            autoFocus
          />
          <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            شامل المؤرشف
          </label>
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {loading ? '⏳' : `${results.length} نتيجة`}
          </span>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">النوع</TableHead>
              <TableHead className="text-right">العنوان</TableHead>
              <TableHead className="text-right">التفاصيل</TableHead>
              <TableHead className="text-right">المبلغ</TableHead>
              <TableHead className="text-right">الموسم</TableHead>
              <TableHead className="text-right">الحالة</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.trim().length < 2 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">اكتب حرفين على الأقل للبحث...</TableCell></TableRow>
            ) : results.length === 0 && !loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">🔍 لا توجد نتائج</TableCell></TableRow>
            ) : results.map((r) => {
              const t = typeLabel[r.type] || { icon: '•', label: r.type, color: 'bg-gray-100' };
              return (
                <TableRow key={`${r.type}-${r.id}`} data-testid={`search-result-${r.type}-${r.id}`}>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${t.color}`}>
                      <span>{t.icon}</span>{t.label}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{r.title}</TableCell>
                  <TableCell className="text-sm text-gray-600">{r.subtitle || '-'}</TableCell>
                  <TableCell className="font-semibold">
                    {r.amount ? `${r.amount.toLocaleString()} دج` : '-'}
                  </TableCell>
                  <TableCell className="text-xs">{r.season || '-'}</TableCell>
                  <TableCell>
                    {r.badge && r.badge !== 'installment'
                      ? <LifecycleBadge status={r.badge} size="xs" />
                      : r.badge === 'installment'
                        ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">قسط</Badge>
                        : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ----------------------------- Tab 2: Seasons -----------------------------
const SeasonsTab = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lifecycle/seasons`, auth());
      setSeasons(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" /> 📅 مركز المواسم</CardTitle>
            <CardDescription>كل المواسم مع حجم البيانات + تقدّم الإغلاق</CardDescription>
          </div>
          <Button variant="outline" onClick={fetchSeasons} size="sm" data-testid="refresh-seasons">
            <RefreshCw className="h-4 w-4 ml-2" /> تحديث
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table data-table-id="seasons-list">
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">الموسم</TableHead>
              <TableHead className="text-right">عدد العمليات</TableHead>
              <TableHead className="text-right">إجمالي المبلغ</TableHead>
              <TableHead className="text-right">🟡 قيد التنفيذ</TableHead>
              <TableHead className="text-right">🔵 مكتمل</TableHead>
              <TableHead className="text-right">⚫ مؤرشف</TableHead>
              <TableHead className="text-right">التقدّم</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8">⏳ جاري التحميل...</TableCell></TableRow>
            ) : seasons.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-gray-500">لا توجد مواسم مسجّلة</TableCell></TableRow>
            ) : seasons.map((s) => {
              const closed = (s.completed_count || 0) + (s.archived_count || 0);
              const pct = s.count > 0 ? Math.round((closed / s.count) * 100) : 0;
              return (
                <TableRow key={s.season} data-testid={`season-row-${s.season}`}>
                  <TableCell className="font-semibold text-brand-700">{s.season}</TableCell>
                  <TableCell><Badge variant="outline">{s.count}</Badge></TableCell>
                  <TableCell className="font-bold text-green-700">{s.total_amount?.toLocaleString()} دج</TableCell>
                  <TableCell><Badge className="bg-yellow-100 text-yellow-800">{s.pending_count}</Badge></TableCell>
                  <TableCell><Badge className="bg-brand-100 text-brand-800">{s.completed_count}</Badge></TableCell>
                  <TableCell><Badge className="bg-gray-200 text-gray-700">{s.archived_count}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-brand-500 to-green-500 h-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{pct}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ----------------------------- Tab 3: Backup + Audit -----------------------------
const BackupAuditTab = () => {
  const [backups, setBackups] = useState([]);
  const [audit, setAudit] = useState([]);
  const [creating, setCreating] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [bRes, aRes] = await Promise.all([
        axios.get(`${API}/lifecycle/backup/list`, auth()),
        axios.get(`${API}/lifecycle/audit-log?limit=50`, auth()),
      ]);
      setBackups(bRes.data || []);
      setAudit(aRes.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createBackup = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/lifecycle/backup/create`, {}, auth());
      alert(`✅ تمّ إنشاء النسخة الاحتياطية:\n${r.data.filename}\n${r.data.documents.toLocaleString()} وثيقة | ${(r.data.size_bytes / 1024).toFixed(1)} KB | مشفّرة AES-256`);
      await fetchAll();
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const actionLabels = {
    archive_bulk: { icon: '📦', label: 'أرشفة جماعية', color: 'text-gray-700' },
    reopen: { icon: '🔓', label: 'إعادة فتح', color: 'text-orange-700' },
    backup_created: { icon: '💾', label: 'نسخة احتياطية', color: 'text-green-700' },
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> 🛡️ النسخ الاحتياطية</CardTitle>
              <CardDescription>كل نسخة مضغوطة بـ gzip ومشفّرة بـ AES-256 (Fernet) — مفتاح من <code>BACKUP_SECRET</code></CardDescription>
            </div>
            <Button
              onClick={createBackup}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="create-backup-btn"
            >
              <Database className="h-4 w-4 ml-2" />
              {creating ? '⏳ جاري الحفظ...' : '💾 إنشاء نسخة احتياطية الآن'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">اسم الملف</TableHead>
                <TableHead className="text-right">الحجم</TableHead>
                <TableHead className="text-right">تاريخ الإنشاء</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">لا توجد نسخ احتياطية بعد</TableCell></TableRow>
              ) : backups.map((b) => (
                <TableRow key={b.filename} data-testid={`backup-${b.filename}`}>
                  <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                  <TableCell>{(b.size_bytes / 1024).toFixed(1)} KB</TableCell>
                  <TableCell className="text-sm">{formatDateWithEnglishNumerals(b.created_at)}</TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-800">🔒 مشفّرة</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">📋 سجل التدقيق</CardTitle>
          <CardDescription>آخر 50 إجراء (أرشفة، إعادة فتح، نسخ احتياطية)</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">الإجراء</TableHead>
                <TableHead className="text-right">المنفذ</TableHead>
                <TableHead className="text-right">التاريخ</TableHead>
                <TableHead className="text-right">التفاصيل</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {audit.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">لا توجد إجراءات مسجّلة</TableCell></TableRow>
              ) : audit.map((a, i) => {
                const cfg = actionLabels[a.action] || { icon: '•', label: a.action, color: 'text-gray-600' };
                return (
                  <TableRow key={i} data-testid={`audit-row-${i}`}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 ${cfg.color}`}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{a.by_name || a.by || '-'}</TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {a.at ? formatDateWithEnglishNumerals(a.at) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {a.action === 'archive_bulk' && a.operation_ids
                        ? `أرشفة ${a.operation_ids.length} عملية`
                        : a.action === 'backup_created'
                          ? `${a.documents?.toLocaleString()} وثيقة`
                          : a.action === 'reopen'
                            ? `العملية: ${a.operation_id}`
                            : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationsHub;
